<script setup lang="ts">
import { applyFiltersMixpanelKey } from '~/composables/useListing/pagination';
import type { PropertyFilters } from '~/core/entities/property/filters';

import { filtersEventsKey } from '~/composables/useListing/filter';
import { PROPERTIES_LISTING_METHODS } from '~~/utils/symbols';
const route = useRoute();

const isMapView = computed<boolean>(
	() => route.name === 'PropertiesMapListing'
);

const { variant, transform, forceFinalVariant } = useHeaderAnimation({
	headerHeightInPx: 80,
	variants: ['primary', 'primary']
});

const filters = ref<PropertyFilters>({
	transactionType: 'sell'
});

const map = useState<PropertiesListingMapState>('properties-listing-map');

const { updateFilters: updateMapFilters } = useMapListing({
	filters: filters.value
});

const macroregionsByAddress: Record<string, string> = {
	'São Paulo': 'Rua Urussuí, 56 - Itaim Bibi, São Paulo - SP',
	Alphaville: 'Alphaville Empresarial - Barueri, SP',
	Interior: 'Condomínio Fazenda Boa Vista - Porto Feliz, SP',
	'Litoral Norte': 'Baleia - São Sebastião, SP',
	'Litoral Sul': 'Jardim Acapulco - Guarujá, SP'
};

function getAddresses(region: string, city: string, state: string): string {
	const parsedRegion = region ? `${region} -` : '';

	if (
		parsedRegion === 'São Paulo, SP' ||
		(!parsedRegion && city === 'São Paulo')
	)
		return macroregionsByAddress['São Paulo']!;

	return `${parsedRegion} ${city}, ${state}`;
}

function fitMap(address: string) {
	const geocoder = new google.maps.Geocoder();

	geocoder.geocode(
		{
			address,
			region: 'BR'
		},
		function (results, status) {
			const result = results?.[0];

			if (status !== 'OK' && !result) return;

			if (result?.geometry.bounds) {
				map.value.mapConfig.bounds = result.geometry.bounds || [];
				return;
			}

			if (result?.geometry.location) {
				map.value.mapConfig.center = result.geometry.location;
			}
		}
	);
}

const filterEvents = useEventBus<{ filters: PropertyFilters }>(
	filtersEventsKey
);

const lastLocationFilters = useState<{
	regions: string[] | null;
	region: string | null;
	city: string | null;
	state: string | null;
	macroregion: string | null;
}>('properties-listing-state', () => ({
	regions: null,
	region: null,
	city: null,
	state: null,
	macroregion: null
}));

function getLocationFilters(
	filters: PropertyFilters
): Record<'region' | 'city' | 'state' | 'macroregion', string> {
	const region = filters.regions?.[0] || '';
	const city = filters.cities?.[0] || '';
	const state = filters.states?.[0] || '';
	const macroregion = filters.macroregions?.[0] || '';

	return { region, city, state, macroregion };
}

function isSameLocationFilters(
	region: string,
	city: string,
	state: string,
	macroregion: string,
	regions?: string[]
): boolean {
	const numberOfNewRegions = regions?.length || 0;
	const numberOfLastRegions = lastLocationFilters.value.regions?.length || 0;

	return (
		numberOfNewRegions === numberOfLastRegions &&
		region === lastLocationFilters.value.region &&
		city === lastLocationFilters.value.city &&
		state === lastLocationFilters.value.state &&
		macroregion === lastLocationFilters.value.macroregion
	);
}

function updateLastLocationFilters(
	region: string,
	city: string,
	state: string,
	macroregion: string,
	regions?: string[]
) {
	lastLocationFilters.value = {
		regions: regions || null,
		region,
		city,
		state,
		macroregion
	};
}

function fitMapFromMacroregion(macroregion: string) {
	const address = macroregionsByAddress[macroregion];

	if (!address) return;

	fitMap(address);
}

function fitMapFromRegionOrCity(region: string, city: string, state: string) {
	const address = getAddresses(region, city, state);

	fitMap(address);
}

function quadrilateralsFiltersChange(
	currentQuadrilaterals: string[] | undefined,
	newQuadrilaterals: string[] | undefined
) {
	if (!currentQuadrilaterals && !newQuadrilaterals) return false;

	return (
		(currentQuadrilaterals?.length || 0) !== (newQuadrilaterals?.length || 0)
	);
}

function handleFiltersChanged(newFilters: PropertyFilters) {
	const hasQuadrilateralFilterChanged: boolean = quadrilateralsFiltersChange(
		filters.value.pluscodesGroup,
		newFilters.pluscodesGroup
	);

	filters.value = newFilters;

	if (hasQuadrilateralFilterChanged || import.meta.server) {
		const { region, city, state, macroregion } = getLocationFilters(newFilters);
		updateLastLocationFilters(
			region,
			city,
			state,
			macroregion,
			newFilters.regions
		);
		return;
	}

	if (newFilters.locationSlug === 'sao-paulo-sp-brasil') {
		fitMapFromMacroregion('São Paulo');
		return;
	}

	const { region, city, state, macroregion } = getLocationFilters(newFilters);

	const hasLocationsNotChanged = isSameLocationFilters(
		region,
		city,
		state,
		macroregion,
		newFilters.regions
	);

	if (hasLocationsNotChanged) return;

	updateLastLocationFilters(
		region,
		city,
		state,
		macroregion,
		newFilters.regions
	);

	if (region || city) {
		fitMapFromRegionOrCity(region, city, state);
		return;
	}

	if (macroregion) {
		fitMapFromMacroregion(macroregion);
	}
}

// TODO: This is being triggered multiple times when navigating client side
// We need to fix this because it's doing multiple requests to google maps
filterEvents.on(({ filters }) => handleFiltersChanged(filters));

onMounted(() => {
	const { macroregion, region, city, state } = lastLocationFilters.value;

	if (region || city) {
		fitMapFromRegionOrCity(region!, city!, state!);
		return;
	}

	if (macroregion) {
		fitMapFromMacroregion(macroregion);
	}
});

provide(PROPERTIES_LISTING_METHODS, {
	updateMapFilters
});
</script>

<template>
	<div class="flex h-dvh flex-col">
		<div>
			<PortalHeader
				:variant="variant"
				:transform="transform"
				:is-visible="isMapView"
				:fixed="isMapView"
				class="h-20"
				:class="{
					'hidden md:sticky md:flex': isMapView,
					'fixed z-50': !isMapView
				}"
				@nav-option-being-shown="forceFinalVariant = true"
				@nav-option-closed="forceFinalVariant = false"
			/>
		</div>
		<div id="filters-bar" :class="{ 'mt-20': !isMapView }" />

		<div id="filters-sidebar" />

		<main
			class="flex grow flex-col"
			:class="{
				'relative h-[calc(100dvh-64px-5.1875rem)] md:h-[calc(100dvh-5rem-5.1875rem)] md:py-0':
					isMapView
			}"
		>
			<slot />
		</main>
	</div>
</template>
